// CommentsSection.tsx — owns the comment thread state, renders header + composer + list.
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDiscussions } from '@/hooks/useDiscussions';
import { discussionsService, type DiscussionState } from '@/core/services/discussionsService';
import { commentsService } from '@/core/services/commentsService';
import { Button } from '@/ui';
import { Hint } from '@/features/discussions/styles';
import styled from 'styled-components';
import { useCommentThread } from './useCommentThread';
import { CommentsHeader } from './CommentsHeader';
import { CommentComposer } from './CommentComposer';
import { CommentList } from './CommentList';

export interface CommentsSectionProps {
  postId: string;
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 8px;
  overflow-anchor: none;
`;

export const CommentsSection = forwardRef<HTMLDivElement, CommentsSectionProps>(
  function CommentsSection({ postId }, ref) {
    const thread = useCommentThread(postId);
    const { owner, prefs } = useDiscussions();
    const [params] = useSearchParams();
    const section = useRef<HTMLElement>(null);
    const initialSnapshot = useRef<string | undefined>(undefined);
    const latestThread = useRef(thread);
    latestThread.current = thread;
    const [state, setState] = useState<DiscussionState | null>(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [target, setTarget] = useState<string | null>(params.get('comment'));
    const [jumpVersion, setJumpVersion] = useState(0);
    const handled = useRef<string | null>(null);
    useEffect(() => {
      if (!owner) return;
      let active = true;
      const refresh = () => {
        if (document.hidden) return;
        void discussionsService
          .post(owner, postId)
          .then((next) => {
            if (active) {
              initialSnapshot.current ??= next.snapshot;
              setState(next);
            }
          })
          .catch(() => {
            /* keep the current reading state during an outage */
          });
      };
      refresh();
      const timer = window.setInterval(refresh, 30_000);
      return () => {
        active = false;
        window.clearInterval(timer);
      };
    }, [owner, postId]);
    useEffect(() => {
      setTarget(params.get('comment'));
    }, [params]);
    useEffect(() => {
      if (!target || handled.current === target || !latestThread.current.reveal(target)) return;
    }, [target, thread.comments, jumpVersion]);
    // Focus only once the reveal has committed the target to the DOM. A frame
    // callback could run between comment hydration and pagination commits.
    useLayoutEffect(() => {
      if (!target || handled.current === target) return;
      const element = document.getElementById(`comment-${target}`);
      if (!element) return;
      element.scrollIntoView({ block: 'center', behavior: 'instant' });
      element.focus({ preventScroll: true });
      handled.current = target;
    });
    useEffect(() => {
      if (
        !owner ||
        !prefs.rememberPosition ||
        !section.current ||
        typeof IntersectionObserver === 'undefined'
      )
        return;
      let last: string | null = null;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          const id = (visible[0]?.target as HTMLElement | undefined)?.dataset.commentId;
          if (!id || id === last || document.hidden) return;
          last = id;
          clearTimeout(timer);
          timer = setTimeout(() => {
            if (document.hidden) return;
            void discussionsService
              .reading(postId, { commentId: id, initialSnapshot: initialSnapshot.current })
              .catch(() => {});
          }, 1200);
        },
        { threshold: 0.6 },
      );
      section.current.querySelectorAll('[data-comment-id]').forEach((el) => observer.observe(el));
      return () => {
        observer.disconnect();
        clearTimeout(timer);
      };
    }, [owner, postId, prefs.rememberPosition, thread.visibleThreads, thread.comments]);
    async function viewNew() {
      if (busy) return;
      setBusy(true);
      setError('');
      try {
        await commentsService.forPost(postId);
        if (state?.firstNewId) {
          handled.current = null;
          setTarget(state.firstNewId);
        }
      } catch {
        setError('Could not load new replies. Try again.');
      } finally {
        setBusy(false);
      }
    }
    async function caughtUp() {
      if (!owner || !state || busy) return;
      setBusy(true);
      setError('');
      try {
        await discussionsService.reading(postId, { snapshot: state.snapshot });
        setState(await discussionsService.post(owner, postId));
        await discussionsService.load(owner, true);
      } catch {
        setError('Could not save your reading state. Try again.');
      } finally {
        setBusy(false);
      }
    }
    return (
      <Section ref={section}>
        <CommentsHeader count={thread.liveCount} sort={thread.sort} onSort={thread.setSort} />
        {owner && state && (
          <div>
            {prefs.rememberPosition && state.resumeCommentId && (
              <Button
                variant="ghost"
                onClick={() => {
                  handled.current = null;
                  setTarget(state.resumeCommentId);
                  setJumpVersion((v) => v + 1);
                }}
              >
                Resume reading
              </Button>
            )}
            {state.newCount > 0 && (
              <Button variant="secondary" disabled={busy} onClick={() => void viewNew()}>
                View {state.newCount} new replies
              </Button>
            )}
            <Button variant="ghost" disabled={busy} onClick={() => void caughtUp()}>
              Mark caught up
            </Button>
            <Hint>
              New replies stay in place until you choose to load them. Reading position is private.
            </Hint>
          </div>
        )}
        {error && <p role="alert">{error}</p>}
        <div ref={ref}>
          <CommentComposer
            key={postId}
            draftScope={`${postId}:post`}
            onSubmit={thread.addTopLevel}
          />
        </div>
        <CommentList thread={thread} />
      </Section>
    );
  },
);
